import { UISelectValue } from '@/ui';

/**
 * Returns a list of IANA timezones for use in dropdown selects.
 */
export function getTimezoneOptions(): UISelectValue[] {
    const timezones = Intl.supportedValuesOf('timeZone');

    const options = timezones.map((tz) => {
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            timeZoneName: 'shortOffset'
        });
        const parts = formatter.formatToParts(new Date());
        const offsetPart = parts.find((p) => p.type === 'timeZoneName');
        const rawOffset = offsetPart?.value ?? 'GMT';

        // Parse "GMT", "GMT+5", "GMT+5:30", "GMT-10" into "(UTC +05:30)" format
        let offsetLabel: string;
        if (rawOffset === 'GMT') {
            offsetLabel = '(UTC +00:00)';
        } else {
            const match = rawOffset.match(/^GMT([+-])(\d{1,2})(?::(\d{2}))?$/);
            if (match) {
                const sign = match[1];
                const hours = match[2].padStart(2, '0');
                const minutes = match[3] ?? '00';
                offsetLabel = `(UTC ${sign}${hours}:${minutes})`;
            } else {
                offsetLabel = '(UTC +00:00)';
            }
        }

        // Extract city name: last segment after "/"
        const city = tz.split('/').pop()!.replace(/_/g, ' ');

        // Numeric offset for sorting
        const numericOffset =
            rawOffset === 'GMT'
                ? 0
                : parseFloat(rawOffset.replace('GMT', '').replace(':', '.'));

        return {
            value: tz,
            label: `${offsetLabel} ${city}`,
            _offset: numericOffset
        };
    });

    options.sort((a, b) => a._offset - b._offset);

    return options.map(({ value, label }) => ({ value, label }));
}

/**
 * Returns the browser's detected timezone (IANA identifier).
 */
export function getBrowserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
